import { ChevronDown, ChevronUp, FileJson, Plus, Upload } from 'lucide-react'
import { LicenseCriteriaImportForm, LicenseCriteriaManualForm } from '../../components/licensing'
import { logger } from '../../utils/logger'
import type { ImportResponse, Organisation, XeroInvoiceState } from '../../types'
import type { CriteriaImportControls, CriteriaImportMode } from './use-criteria-tab-state'

interface CriteriaImportSectionProps {
  controls: CriteriaImportControls
  organisations: Organisation[]
  xeroInvoices: XeroInvoiceState[]
}

interface ModeButtonProps {
  active: boolean
  label: string
  icon: React.ReactNode
  onClick: () => void
}

function ModeButton({ active, label, icon, onClick }: ModeButtonProps): React.ReactElement {
  const className = active
    ? 'bg-[#5c6e94] text-white'
    : 'border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700'
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${className}`}
    >
      {icon}
      {label}
    </button>
  )
}

interface SectionHeaderProps {
  expanded: boolean
  onToggle: () => void
}

function SectionHeader({ expanded, onToggle }: SectionHeaderProps): React.ReactElement {
  return (
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between px-6 py-4 text-left transition-colors hover:bg-stone-50 dark:hover:bg-stone-700/50"
    >
      <div className="flex items-center gap-3">
        <Upload className="h-5 w-5 text-stone-500 dark:text-stone-400" />
        <div>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
            Import License Criteria
          </h2>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            Import criteria from JSON file or enter manually
          </p>
        </div>
      </div>
      {expanded ? (
        <ChevronUp className="h-5 w-5 text-stone-400" />
      ) : (
        <ChevronDown className="h-5 w-5 text-stone-400" />
      )}
    </button>
  )
}

interface ImportFormsProps extends CriteriaImportSectionProps {
  onImportSuccess: (result: ImportResponse) => void
  onManualSuccess: () => void
}

function handleImportError(error: Error): void {
  logger.error('CriteriaImportSection', 'Import error', error)
}

function ImportForms(props: ImportFormsProps): React.ReactElement {
  const { controls, organisations, xeroInvoices, onImportSuccess, onManualSuccess } = props
  return (
    <>
      {controls.importMode === 'json' && (
        <LicenseCriteriaImportForm onSuccess={onImportSuccess} onError={handleImportError} />
      )}
      {controls.importMode === 'manual' && (
        <LicenseCriteriaManualForm
          organisations={organisations}
          xeroInvoices={xeroInvoices}
          onSuccess={onManualSuccess}
          onError={handleImportError}
        />
      )}
    </>
  )
}

export function CriteriaImportSection(props: CriteriaImportSectionProps): React.ReactElement {
  const { controls } = props
  const setMode = (target: CriteriaImportMode): void => {
    controls.setImportMode(controls.importMode === target ? 'none' : target)
  }
  const handleImportSuccess = (result: ImportResponse): void => {
    void controls.finishImport(result)
  }
  const handleManualSuccess = (): void => {
    void controls.finishImport()
  }
  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <SectionHeader
        expanded={controls.showImportSection}
        onToggle={controls.toggleShowImportSection}
      />
      {controls.showImportSection && (
        <div className="border-t border-stone-200 px-6 pb-6 dark:border-stone-700">
          <div className="flex gap-3 pt-4 pb-4">
            <ModeButton
              active={controls.importMode === 'json'}
              label="Import JSON"
              icon={<FileJson className="h-4 w-4" />}
              onClick={() => {
                setMode('json')
              }}
            />
            <ModeButton
              active={controls.importMode === 'manual'}
              label="Manual Entry"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setMode('manual')
              }}
            />
          </div>
          <ImportForms
            {...props}
            onImportSuccess={handleImportSuccess}
            onManualSuccess={handleManualSuccess}
          />
        </div>
      )}
    </div>
  )
}
